// src/components/DisciplineLadderPanel.js
//
// Record 611 (F20; Bob 10/8) - the discipline ladder's acts on the case screen: Open (a draft), Advance, Record
// decision, Resolve, Reopen. Each is a recorded act of the Cloud Function `disciplineAct` (functions\index.js); this
// page never writes a case's status, number, decision or forward stamps (firestore.rules refuses them to every
// browser). Each control is OFFERED only where may() in src\data\disciplineLadder.js allows it - the same code the
// function decides with - and every refusal is said on screen; nothing fails silently.
//
//   - callDisciplineAct() is the ONE place in src that calls httpsCallable(..., 'disciplineAct'). DisciplineCases.js
//     uses it for "Open Discipline Case" (the new-case form) and for the Forward button.
//   - Record decision: outcome (the decision vocabulary), decision date, notes, and an optional letter of decision,
//     uploaded with the case screen's upload pattern (discipline/<caseId>/decision_<time>_<name>, tagged by
//     disciplineObjectTag, the storagePath kept - Record 591: the link is fetched only when View is clicked).
//   - Resolve: from Decision Pending, or from GC Review once forwarded; the decision must be on record first.
//   - Reopen: the General Chairperson or the administrator, with a reason; back to GC Review if the case was ever
//     forwarded, else to Decision Pending.
// Nothing here runs on load: the callable is reached only from a click.
import React, { useState } from 'react';
import { storage, auth } from '../firebase';
import { ref, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  may, ladderActor, validateDecision, validateReason, hasDecision, DECISION_OUTCOMES, STAGE_LABELS, NOTES_MAX, REASON_MAX,
  DRAFT, RESOLVED,
} from '../data/disciplineLadder';
import { disciplineObjectTag } from '../data/disciplineStorage';
import { ladderAgainstActs } from '../data/disciplineStageFromActs';   // T5 / D-22: the stage the acts support

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', ok: '#5FB878', text: '#DCE7F2', bad: '#E06C6C', panel: '#0A1628' };

// The caller as may() reads him, from the page's user object (App.js builds it from the token's custom claims).
export function ladderCaller(user) {
  const u = user || {};
  return ladderActor({
    uid: u.uid || (auth.currentUser && auth.currentUser.uid) || '',
    role: u.isAdmin ? 'admin' : u.role,
    committee: u.committee, scopes: u.scopes, name: u.name, email: u.email || (auth.currentUser && auth.currentUser.email) || '',
  });
}

// THE ONE CALL SITE. Returns the function's answer, or throws with its message.
export async function callDisciplineAct(payload) {
  const fn = httpsCallable(getFunctions(), 'disciplineAct');
  const r = await fn(payload);
  return (r && r.data) || {};
}

const OFFICER_ROLES = ['admin', 'general_chair', 'local_chair'];

export default function DisciplineLadderPanel({ kase, caseId, user }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [deciding, setDeciding] = useState(null);   // null = the form is closed
  const [letter, setLetter] = useState(null);
  const [letterKey, setLetterKey] = useState(0);
  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState('');

  if (!kase || !caseId) return null;
  const me = ladderCaller(user);
  const adv = may(me, 'advance', kase);
  const dec = may(me, 'decide', kase);
  const res = may(me, 'resolve', kase);
  const reo = may(me, 'reopen', kase);
  const status = String(kase.status || '');
  const officer = OFFICER_ROLES.indexOf(me.role) !== -1;

  const run = async (kind, extra, label) => {
    setBusy(kind); setErr(''); setMsg('');
    try {
      const r = await callDisciplineAct(Object.assign({ caseId: caseId, kind: kind }, extra || {}));
      setMsg(label + (r && r.numbered && r.caseNumber ? ' Case number ' + r.caseNumber + ' assigned.' : ''));
      return r;
    } catch (e) {
      setErr(label.replace(/\.$/, '') + ' - NOT recorded: ' + ((e && e.message) || 'the act was refused') + '. Nothing on the case was changed.');
      return null;
    } finally { setBusy(''); }
  };

  const advance = async () => {
    if (status === DRAFT && !window.confirm('Open this case? It is numbered from your committee\'s counter and moves to Notice Received, where the Local Chairperson of the territory and the General Chairperson of the committee can see it.')) return;
    await run('advance', null, status === DRAFT ? 'Case opened.' : 'Advanced to ' + (STAGE_LABELS[adv.to] || adv.to) + '.');
  };

  const openDecision = () => {
    if (deciding) { setDeciding(null); return; }
    setDeciding({
      outcome: DECISION_OUTCOMES.indexOf(String(kase.decision || '')) !== -1 ? String(kase.decision) : '',
      decisionDate: String(kase.decisionDate || ''),
      notes: String(kase.decisionNotes || ''),
      terms: Object.assign({ suspensionDays: '', demerits: '', abeyanceMonths: '', effectiveDate: '' },
        Object.fromEntries(Object.entries(kase.decisionTerms || {}).map(([k2, v]) => [k2, v === null || v === undefined ? '' : String(v)]))),   // T5 / D-15
    });
    setLetter(null); setLetterKey(k => k + 1);
  };

  const recordDecision = async () => {
    const f = deciding || {};
    const pre = validateDecision({ outcome: f.outcome, decisionDate: f.decisionDate, notes: f.notes, terms: f.terms }, caseId);   // T5 / D-15: terms
    if (!pre.ok) { setErr(pre.problems.join(' ')); return; }
    setBusy('decide'); setErr(''); setMsg('');
    let letterPath = '';
    if (letter) {
      try {
        const storageRef = ref(storage, `discipline/${caseId}/decision_${Date.now()}_${letter.name}`);
        await uploadBytes(storageRef, letter, disciplineObjectTag(kase));
        letterPath = storageRef.fullPath;   // Record 591 (ruling 247): the path is kept; View fetches the link after the scan
      } catch (e) {
        setBusy('');
        setErr('The letter of decision could not be stored (' + ((e && e.code) || (e && e.message) || 'unknown') + '). The decision was NOT recorded.');
        return;
      }
    }
    const r = await run('decide', { outcome: pre.value.outcome, decisionDate: pre.value.decisionDate, notes: pre.value.notes, letterPath: letterPath, terms: pre.value.terms },
      'Decision recorded.');
    if (r) { setDeciding(null); setLetter(null); setLetterKey(k => k + 1); }
  };

  const resolve = async () => {
    if (!window.confirm('Resolve this case?\n\nThe decision on record: ' + String(kase.decision || '') + (kase.decisionDate ? ', dated ' + kase.decisionDate : '')
      + '.\n\nOnly the General Chairperson or the administrator can reopen it afterwards, with a reason.')) return;
    await run('resolve', null, 'Case resolved.');
  };

  const reopen = async () => {
    const r0 = validateReason(reason);
    if (!r0.ok) { setErr(r0.problem); return; }
    const r = await run('reopen', { reason: r0.value }, 'Case reopened.');
    if (r) { setReason(''); setReopening(false); }
  };

  const nothing = !adv.ok && !dec.ok && !res.ok && !reo.ok;
  // The one refusal worth saying to an officer who is offered nothing: the resolve's, when only the decision is missing;
  // otherwise the advance's.
  const hint = dec.ok && !res.ok && (status === 'decision_pending' || status === 'gc_review') ? res.because
    : (nothing && officer && status !== RESOLVED ? adv.because : '');
  const btn = { fontSize: 12, padding: '6px 16px' };

  return (
    <div style={{ marginTop: 12 }}>
      {hasDecision(kase) ? (
        <div style={{ fontSize: 12, color: C.text, marginBottom: 8 }}>
          Decision on record: <strong>{String(kase.decision)}</strong>{kase.decisionDate ? ', dated ' + kase.decisionDate : ''}
          {kase.decidedByName ? <span style={{ color: C.dim }}> - recorded by {kase.decidedByName}</span> : null}
        </div>
      ) : null}
      {status === RESOLVED && kase.resolvedByName ? <div style={{ fontSize: 12, color: C.dim, marginBottom: 6 }}>Resolved by {kase.resolvedByName}.</div> : null}
      {kase.reopenReason && status !== RESOLVED ? <div style={{ fontSize: 12, color: C.accent, marginBottom: 6 }}>Reopened by {kase.reopenedByName || 'an officer'}: {kase.reopenReason}</div> : null}

      {(() => { const cmp = ladderAgainstActs(kase); return cmp ? <div style={{ fontSize: 12, color: cmp.agree ? C.dim : C.accent, marginBottom: 6 }}>{cmp.agree ? '' : '⚠ '}{cmp.line}</div> : null; })()}   {/* T5 / D-22 */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {adv.ok ? <button className="btn btn-primary" style={btn} disabled={!!busy} onClick={advance}>
          {busy === 'advance' ? 'Recording...' : status === DRAFT ? 'Open this case (Notice Received) →' : 'Advance to ' + (STAGE_LABELS[adv.to] || adv.to) + ' →'}
        </button> : null}
        {dec.ok ? <button className="btn btn-secondary" style={btn} disabled={!!busy} onClick={openDecision}>
          {hasDecision(kase) ? 'Amend the decision' : 'Record decision'}
        </button> : null}
        {res.ok ? <button className="btn btn-primary" style={btn} disabled={!!busy} onClick={resolve}>
          {busy === 'resolve' ? 'Recording...' : 'Resolve the case'}
        </button> : null}
        {reo.ok ? <button className="btn btn-secondary" style={btn} disabled={!!busy} onClick={() => setReopening(!reopening)}>Reopen...</button> : null}
      </div>

      {deciding && dec.ok ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10, padding: 10, border: '1px solid ' + C.line, background: C.panel }}>
          <div style={{ gridColumn: '1 / span 2', fontSize: 12, fontWeight: 700, color: C.accent }}>Record the decision</div>
          <select aria-label="Outcome" className="form-select" value={deciding.outcome} onChange={e => setDeciding({ ...deciding, outcome: e.target.value })}>
            <option value="">Outcome...</option>
            {DECISION_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <input aria-label="Decision date" className="form-input" type="date" value={deciding.decisionDate} title="The date of the decision" onChange={e => setDeciding({ ...deciding, decisionDate: e.target.value })} />
          {/* T5 / D-15: the terms - what was assessed. Blank = not recorded, never zero. */}
          {(deciding.outcome === 'Suspension' || deciding.outcome === 'Discipline Reduced') ? <input aria-label="Suspension days" className="form-input" inputMode="numeric" placeholder="Suspension days" value={(deciding.terms || {}).suspensionDays || ''} onChange={e => setDeciding({ ...deciding, terms: { ...(deciding.terms || {}), suspensionDays: e.target.value } })} /> : null}
          {(deciding.outcome === 'Demerit' || deciding.outcome === 'Discipline Reduced') ? <input aria-label="Demerits" className="form-input" inputMode="numeric" placeholder="Demerits" value={(deciding.terms || {}).demerits || ''} onChange={e => setDeciding({ ...deciding, terms: { ...(deciding.terms || {}), demerits: e.target.value } })} /> : null}
          {/Abeyance/.test(deciding.outcome || '') ? <input aria-label="Months held in abeyance" className="form-input" inputMode="numeric" placeholder="Months held in abeyance" value={(deciding.terms || {}).abeyanceMonths || ''} onChange={e => setDeciding({ ...deciding, terms: { ...(deciding.terms || {}), abeyanceMonths: e.target.value } })} /> : null}
          <label style={{ fontSize: 11, color: C.dim }}>Effective date (optional) <input aria-label="Effective date of the decision" className="form-input" type="date" value={(deciding.terms || {}).effectiveDate || ''} onChange={e => setDeciding({ ...deciding, terms: { ...(deciding.terms || {}), effectiveDate: e.target.value } })} /></label>
          <textarea aria-label="Notes (optional)" className="form-textarea" style={{ gridColumn: '1 / span 2' }} rows={3} maxLength={NOTES_MAX} placeholder="Notes (optional)"
            value={deciding.notes} onChange={e => setDeciding({ ...deciding, notes: e.target.value })} />
          <div style={{ gridColumn: '1 / span 2', fontSize: 12, color: C.dim }}>
            Letter of decision (optional):{' '}
            <input aria-label="Attach the decision letter (PDF or Word)" key={letterKey} type="file" accept=".pdf,.doc,.docx" onChange={e => setLetter((e.target.files && e.target.files[0]) || null)} />
          </div>
          <div style={{ gridColumn: '1 / span 2', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" style={btn} disabled={!!busy} onClick={() => setDeciding(null)}>Cancel</button>
            <button className="btn btn-primary" style={btn} disabled={!!busy || !deciding.outcome || !deciding.decisionDate} onClick={recordDecision}>
              {busy === 'decide' ? 'Recording...' : 'Record decision'}
            </button>
          </div>
        </div>
      ) : null}

      {reopening && reo.ok ? (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid ' + C.line, background: C.panel }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 6 }}>
            Reopen the case - it returns to {STAGE_LABELS[reo.to] || reo.to}. The reason goes on the case and in its notes.
          </div>
          <textarea aria-label="Why the case is reopened (required)" className="form-textarea" rows={3} maxLength={REASON_MAX} placeholder="Why the case is reopened (required)"
            value={reason} onChange={e => setReason(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
            <button className="btn btn-secondary" style={btn} disabled={!!busy} onClick={() => { setReopening(false); setReason(''); }}>Cancel</button>
            <button className="btn btn-primary" style={btn} disabled={!!busy || !reason.trim()} onClick={reopen}>
              {busy === 'reopen' ? 'Recording...' : 'Reopen the case'}
            </button>
          </div>
        </div>
      ) : null}

      {hint ? <div style={{ fontSize: 11, color: C.dim, marginTop: 6, textAlign: 'right' }}>{hint}</div> : null}
      {err ? <div style={{ fontSize: 12, color: C.bad, marginTop: 8 }}>&#9888; {err}</div> : null}
      {msg ? <div style={{ fontSize: 12, color: C.ok, marginTop: 8 }}>{msg}</div> : null}
    </div>
  );
}
